import { useEffect, useState } from "react";
import { useCachedState } from "@/hooks/use-cached-state";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { TableSkeleton } from "@/components/loading-skeletons";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { UserPlus, Loader2, Trash2, Shield, Banknote, GraduationCap, Mail, Camera } from "lucide-react";
import { toast } from "sonner";
import { inviteMember, removeMember } from "@/lib/members.functions";
import { useUserRole, type AppRole } from "@/hooks/use-user-role";
import { uploadAvatar } from "@/lib/avatar";
import { MemberAvatar } from "@/components/member-avatar";
import { useRef } from "react";

export const Route = createFileRoute("/_authenticated/members")({
  head: () => ({ meta: [{ title: "Anggota & Akses — MI Plus Almunajah" }] }),
  component: MembersPage,
});

type Member = {
  user_id: string;
  email: string | null;
  full_name: string | null;
  role: AppRole | null;
  avatar_url: string | null;
  created_at: string;
};

const ROLE_LABEL: Record<AppRole, string> = {
  superadmin: "Super Admin",
  admin: "Admin",
  bendahara: "Bendahara",
  guru: "Guru",
};

const ROLE_TONE: Record<AppRole, string> = {
  superadmin: "bg-primary/15 text-primary",
  admin: "bg-info/15 text-info",
  bendahara: "bg-warning/15 text-warning",
  guru: "bg-success/15 text-success",
};

const ROLE_ICON: Record<AppRole, React.ReactNode> = {
  superadmin: <Shield className="w-3 h-3 mr-1" />,
  admin: <Shield className="w-3 h-3 mr-1" />,
  bendahara: <Banknote className="w-3 h-3 mr-1" />,
  guru: <GraduationCap className="w-3 h-3 mr-1" />,
};

function MembersPage() {
  const { role: myRole, loading: roleLoading } = useUserRole();
  const [rows, setRows] = useCachedState<Member[]>("members:rows", []);
  const [loading, setLoading] = useState(true);

  const invite = useServerFn(inviteMember);
  const remove = useServerFn(removeMember);

  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState<{ email: string; full_name: string; role: AppRole }>({
    email: "", full_name: "", role: "guru",
  });

  const load = async (showLoading = false) => {
    if (showLoading) setLoading(true);
    const { data, error } = await supabase.rpc("list_members");
    if (showLoading) setLoading(false);
    if (error) return toast.error(error.message);
    setRows((data ?? []) as Member[]);
  };

  useEffect(() => { load(true); }, []);

  const onInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email || !form.full_name) return toast.error("Isi nama dan email.");
    setSubmitting(true);
    try {
      await invite({
        data: {
          email: form.email,
          full_name: form.full_name,
          role: form.role,
          redirect_to: "https://almunajah.web.id/create-password",
        },
      });
      toast.success(`Undangan dikirim ke ${form.email}`);
      setOpen(false);
      setForm({ email: "", full_name: "", role: "guru" });
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Gagal mengirim undangan");
    } finally {
      setSubmitting(false);
    }
  };

  const onRemove = async (m: Member) => {
    try {
      await remove({ data: { user_id: m.user_id } });
      toast.success("Anggota dihapus");
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Gagal menghapus");
    }
  };

  const onResetPassword = async (m: Member) => {
    if (!m.email) return;
    const { error } = await supabase.auth.resetPasswordForEmail(m.email, {
      redirectTo: "https://almunajah.web.id/reset-password",
    });
    if (error) {
      toast.error(error.message);
    } else {
      toast.success(`Email reset password telah dikirim ke ${m.email}`);
    }
  };

  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({});
  const [uploadingId, setUploadingId] = useState<string | null>(null);

  const onPickAvatar = (userId: string) => fileInputs.current[userId]?.click();

  const onAvatarChange = async (m: Member, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("File harus berupa gambar.");
    if (file.size > 3 * 1024 * 1024) return toast.error("Ukuran maksimal 3 MB.");
    setUploadingId(m.user_id);
    try {
      const path = await uploadAvatar(m.user_id, file);
      const { error } = await supabase.rpc("admin_set_member_avatar", {
        target_user_id: m.user_id,
        new_avatar_path: path,
      });
      if (error) throw new Error(error.message);
      toast.success("Foto profil diperbarui");
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Gagal mengunggah foto");
    } finally {
      setUploadingId(null);
    }
  };

  const onChangeRole = async (m: Member, newRole: AppRole) => {
    if (newRole === m.role) return;
    const { error } = await supabase.rpc("admin_set_user_role", {
      target_user_id: m.user_id,
      new_role: newRole,
    });
    if (error) return toast.error(error.message);
    toast.success(`Role diperbarui menjadi ${ROLE_LABEL[newRole]}`);
    await load();
  };

  if (!roleLoading && myRole !== "admin" && myRole !== "superadmin") {
    return <div className="text-muted-foreground">Akses ditolak.</div>;
  }

  return (
    <div>
      <PageHeader
        title="Anggota & Akses"
        description="Undang admin, bendahara, atau guru. Atur peran tiap anggota."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-xl"><UserPlus className="w-4 h-4 mr-2" /> Undang Anggota</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Undang Anggota Baru</DialogTitle>
              </DialogHeader>
              <form onSubmit={onInvite} className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Nama Lengkap</Label>
                  <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required maxLength={120} />
                </div>
                <div className="space-y-1.5">
                  <Label>Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required maxLength={255} />
                </div>
                <div className="space-y-1.5">
                  <Label>Peran</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v as AppRole })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="bendahara">Bendahara</SelectItem>
                      <SelectItem value="guru">Guru</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={submitting} className="rounded-xl">
                    {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Kirim Undangan
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <Card className="rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">Foto</TableHead>
              <TableHead>Nama</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Peran</TableHead>
              <TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableSkeleton columns={5} />
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Belum ada anggota.</TableCell></TableRow>
            ) : rows.map((m) => (
              <TableRow key={m.user_id}>
                <TableCell>
                  <button
                    type="button"
                    onClick={() => onPickAvatar(m.user_id)}
                    className="relative group rounded-full focus:outline-none focus:ring-2 focus:ring-ring"
                    title="Ubah foto profil"
                  >
                    <MemberAvatar path={m.avatar_url} name={m.full_name} role={m.role} className="w-10 h-10" />
                    <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity text-white">
                      {uploadingId === m.user_id
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <Camera className="w-4 h-4" />}
                    </span>
                    <input
                      ref={(el) => { fileInputs.current[m.user_id] = el; }}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => onAvatarChange(m, e)}
                    />
                  </button>
                </TableCell>
                <TableCell className="font-medium">{m.full_name || "-"}</TableCell>
                <TableCell className="text-muted-foreground">{m.email || "-"}</TableCell>
                <TableCell>
                  {m.role ? (
                  <Badge variant="secondary" className={ROLE_TONE[m.role]}>
                      {ROLE_ICON[m.role]} {ROLE_LABEL[m.role]}
                    </Badge>
                  ) : <span className="text-muted-foreground">-</span>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex items-center gap-2">
                    {m.role !== "superadmin" && (
                      <Select value={m.role ?? undefined} onValueChange={(v) => onChangeRole(m, v as AppRole)}>
                        <SelectTrigger className="h-8 w-32"><SelectValue placeholder="Ubah role" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="bendahara">Bendahara</SelectItem>
                          <SelectItem value="guru">Guru</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    {m.role !== "superadmin" && m.email && (
                      <Button
                        size="icon"
                        variant="ghost"
                        title="Kirim email reset password"
                        onClick={() => onResetPassword(m)}
                      >
                        <Mail className="w-4 h-4" />
                      </Button>
                    )}
                    {m.role !== "superadmin" && (m.role !== "admin" || myRole === "superadmin") && m.user_id && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="icon" variant="ghost" className="text-destructive"><Trash2 className="w-4 h-4" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Hapus Anggota</AlertDialogTitle>
                            <AlertDialogDescription>
                              Akun {m.email} akan dihapus permanen dari sistem.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Batal</AlertDialogCancel>
                            <AlertDialogAction onClick={() => onRemove(m)} className="bg-destructive text-destructive-foreground">Hapus</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
